import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api/client'
import { Logo } from '../../components/Logo'
import { useToast } from '../../hooks/useToast'
import {
  TIER_NAME,
  bandColor,
  displayName,
  stageLabel,
  tierColor,
  tierLabel,
} from '../../lib/format'
import type { BriefSummary, Organisation, OrganisationPage, Sector } from '../../types'

type SortKey = 'name' | 'seg' | 'tier' | 'web' | 'status'

export function AccountsView() {
  const navigate = useNavigate()
  const toast = useToast()

  const [sectors, setSectors] = useState<Sector[]>([])
  const [briefs, setBriefs] = useState<BriefSummary[]>([])
  const [group, setGroup] = useState<'private' | 'public'>('private')
  const [sectorId, setSectorId] = useState('insurance')

  const [page, setPage] = useState<OrganisationPage>({ rows: [], total: 0, segments: [] })
  const [loading, setLoading] = useState(true)

  const [q, setQ] = useState('')
  const [segment, setSegment] = useState('')
  const [tier, setTier] = useState('')

  const [view, setView] = useState<'table' | 'cards'>('table')
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'tier', dir: 1 })

  useEffect(() => {
    Promise.all([api.sectors(), api.briefs()])
      .then(([s, b]) => {
        setSectors(s)
        setBriefs(b)
      })
      .catch((e) => toast(e.message))
  }, [toast])

  // Filtering runs in Postgres — 2,811 organisations never all cross the wire.
  useEffect(() => {
    setLoading(true)
    const timer = window.setTimeout(() => {
      api
        .organisations(sectorId, { segment, tier, q })
        .then(setPage)
        .catch((e) => toast(e.message))
        .finally(() => setLoading(false))
    }, 180) // debounce the filter box
    return () => window.clearTimeout(timer)
  }, [sectorId, segment, tier, q, toast])

  const sector = sectors.find((s) => s.id === sectorId)
  const groupSectors = sectors.filter((s) => s.group === group)
  const groupTotal = (g: string) =>
    sectors.filter((s) => s.group === g).reduce((n, s) => n + s.count, 0)

  const hot = useMemo(
    () =>
      briefs
        .filter((b) => (b.score ?? 0) >= 70)
        .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
        .slice(0, 8),
    [briefs],
  )

  function switchGroup(g: 'private' | 'public') {
    if (g === group) return
    setGroup(g)
    const first = sectors.find((s) => s.group === g)
    if (first) setSectorId(first.id)
    resetFilters()
  }

  function resetFilters() {
    setQ('')
    setSegment('')
    setTier('')
  }

  /** Open the account if it's been briefed; otherwise hand off to Research, pre-filled. */
  function open(org: Organisation) {
    if (org.briefed) {
      navigate(`/accounts/${encodeURIComponent(org.name)}`)
      return
    }
    const params = new URLSearchParams({
      company: org.name,
      website: org.website ?? '',
      sector: sector?.appSector ?? '',
    })
    navigate(`/research?${params}`)
  }

  const sorted = useMemo(() => {
    const rows = [...page.rows]
    const { key, dir } = sort
    rows.sort((a, b) => {
      let av: string | number
      let bv: string | number
      if (key === 'tier') {
        av = a.tier
        bv = b.tier
      } else if (key === 'status') {
        av = a.briefed ? 0 : 1
        bv = b.briefed ? 0 : 1
      } else {
        const field = key === 'seg' ? 'segment' : key === 'web' ? 'website' : 'name'
        av = (a[field] ?? '').toString().toLowerCase()
        bv = (b[field] ?? '').toString().toLowerCase()
      }
      if (av < bv) return -1 * dir
      if (av > bv) return 1 * dir
      return a.name.localeCompare(b.name)
    })
    return rows
  }, [page.rows, sort])

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: 1 }))
  }

  function exportCsv() {
    const rows = [
      ['Organisation', 'Tier', 'Segment', 'Website', 'Sector'],
      ...page.rows.map((r) => [r.name, tierLabel(r.tier), r.segment, r.website ?? '', sector?.label ?? '']),
    ]
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n')
    const blob = new Blob(['﻿', csv], { type: 'text/csv' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `burendo-targets-${sectorId}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
    toast(`${page.rows.length} ${sector?.label ?? ''} organisations exported`)
  }

  const arrow = (k: SortKey) => (sort.key === k ? (sort.dir > 0 ? '▲' : '▼') : '↕')

  return (
    <div className="view on">
      <h1 className="disp">Accounts</h1>
      <p className="lede">Pick a sector, filter, or click any organisation for an instant brief.</p>

      {hot.length > 0 && (
        <div>
          <div className="hotstrip-lbl">Hot right now</div>
          <div className="hotstrip-row">
            {hot.map((b) => (
              <button
                key={b.id}
                className="hotchip"
                onClick={() => navigate(`/accounts/${encodeURIComponent(b.company)}`)}
              >
                <Logo company={b.company} logo={b.logo} size={20} />
                <span className="hc-name">{displayName(b.company)}</span>
                <span className="hc-score" style={{ background: bandColor(b.score) }}>
                  {b.score}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ margin: '20px 0 4px' }}>
        <div className="grp-toggle">
          <button
            className={`grp-btn${group === 'private' ? ' on' : ''}`}
            onClick={() => switchGroup('private')}
          >
            Private Sector <span>{groupTotal('private')}</span>
          </button>
          <button
            className={`grp-btn${group === 'public' ? ' on' : ''}`}
            onClick={() => switchGroup('public')}
          >
            Public Sector <span>{groupTotal('public')}</span>
          </button>
        </div>

        <div className="sec-grid">
          {groupSectors.map((s) => (
            <button
              key={s.id}
              className={`sec-card${s.id === sectorId ? ' on' : ''}`}
              onClick={() => {
                setSectorId(s.id)
                resetFilters()
              }}
            >
              <span className="sec-dot" />
              <i className={`ti ${s.icon ?? 'ti-briefcase'} sec-ico`} aria-hidden="true" />
              <span className="sec-label">{s.label}</span>
              <span className="sec-ct">{s.count.toLocaleString()} organisations</span>
            </button>
          ))}
        </div>
      </div>

      <div className="libtools" style={{ marginTop: 14 }}>
        <input
          className="tx"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter companies…"
        />
        <select className="tx" value={segment} onChange={(e) => setSegment(e.target.value)}>
          <option value="">All segments</option>
          {page.segments.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <select className="tx" style={{ width: 150 }} value={tier} onChange={(e) => setTier(e.target.value)}>
          <option value="">All tiers</option>
          <option value="1">Tier 1</option>
          <option value="2">Tier 2</option>
          <option value="3">Tier 3</option>
          <option value="4">Tier 4 (approved)</option>
        </select>
      </div>

      <div className="tgttools">
        <span className="tgt-count">
          {loading
            ? 'Loading…'
            : `${page.rows.length.toLocaleString()} of ${page.total.toLocaleString()} organisations · ${sector?.label ?? ''}`}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <div className="viewtoggle">
            <button
              className={`vt-btn${view === 'table' ? ' on' : ''}`}
              title="Table view"
              onClick={() => setView('table')}
            >
              <i className="ti ti-table" />
            </button>
            <button
              className={`vt-btn${view === 'cards' ? ' on' : ''}`}
              title="Card view"
              onClick={() => setView('cards')}
            >
              <i className="ti ti-layout-grid" />
            </button>
          </div>
          <button className="csvbtn" onClick={exportCsv}>
            ↓ Export sector (CSV)
          </button>
        </div>
      </div>

      {!loading && sorted.length === 0 && (
        <div className="empty">
          <div className="ico">⌕</div>
          No organisations match.
        </div>
      )}

      {sorted.length > 0 && view === 'table' && (
        <div className="acctbl-wrap">
          <table className="acctbl">
            <thead>
              <tr>
                <th className={sort.key === 'name' ? 'sorted' : ''} onClick={() => toggleSort('name')}>
                  Organisation <span className="arr">{arrow('name')}</span>
                </th>
                <th
                  className={`col-seg ${sort.key === 'seg' ? 'sorted' : ''}`}
                  onClick={() => toggleSort('seg')}
                >
                  Segment <span className="arr">{arrow('seg')}</span>
                </th>
                <th className={sort.key === 'tier' ? 'sorted' : ''} onClick={() => toggleSort('tier')}>
                  Tier <span className="arr">{arrow('tier')}</span>
                </th>
                <th
                  className={`col-web ${sort.key === 'web' ? 'sorted' : ''}`}
                  onClick={() => toggleSort('web')}
                >
                  Website <span className="arr">{arrow('web')}</span>
                </th>
                <th className={sort.key === 'status' ? 'sorted' : ''} onClick={() => toggleSort('status')}>
                  Status <span className="arr">{arrow('status')}</span>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={`${r.name}-${r.tier}`} onClick={() => open(r)}>
                  <td>
                    <span className="c-name">{r.name}</span>
                  </td>
                  <td className="col-seg">
                    <span className="c-seg">{r.segment}</span>
                  </td>
                  <td>
                    <span className="tierdot" style={{ background: tierColor(r.tier) }}>
                      {tierLabel(r.tier)}
                    </span>
                  </td>
                  <td className="col-web">
                    <span className="c-web">
                      {r.website || '—'}
                      {r.websiteGuessed && <span style={{ color: 'var(--warm)' }}> · guessed</span>}
                    </span>
                  </td>
                  <td>
                    {r.briefed ? (
                      <span className="st-done">✓ {stageLabel(r.stage)}</span>
                    ) : (
                      <span className="st-new">Not briefed</span>
                    )}
                  </td>
                  <td>
                    <span className="rowgo">{r.briefed ? 'Open →' : 'Brief →'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sorted.length > 0 && view === 'cards' && (
        <CardGroups rows={sorted} onOpen={open} />
      )}

      <p className="note">
        A green ✓ marks accounts you've already briefed — clicking reopens the saved brief. Tier 1 = major /
        household names; Tier 2 = mid-market specialists; Tier 3 = niche, challengers &amp; emerging.
      </p>
    </div>
  )
}

function CardGroups({ rows, onOpen }: { rows: Organisation[]; onOpen: (o: Organisation) => void }) {
  const tiers: Record<number, Organisation[]> = { 1: [], 2: [], 3: [], 4: [] }
  rows.forEach((r) => tiers[r.tier]?.push(r))

  return (
    <>
      {[1, 2, 3, 4]
        .filter((t) => tiers[t].length)
        .map((t) => (
          <div className="tgtgroup" key={t}>
            <div className="tgtgroup-h">
              <span className={`tierbadge t${t}`}>{tierLabel(t)}</span>{' '}
              {t === 4 ? 'Approved organisations — public-sector framework register' : TIER_NAME[t]}{' '}
              <span>{tiers[t].length}</span>
            </div>
            <div className="tgtgrid">
              {tiers[t]
                .slice()
                .sort((a, b) => a.segment.localeCompare(b.segment) || a.name.localeCompare(b.name))
                .map((r) => (
                  <button className="tgt" key={r.name} onClick={() => onOpen(r)}>
                    <span className="tn">
                      {r.name}
                      {r.briefed && (
                        <span style={{ color: 'var(--good)', fontWeight: 700 }} title="Brief already generated">
                          {' '}
                          ✓
                        </span>
                      )}
                    </span>
                    <span className="tw">
                      {r.website ? (
                        <>
                          {r.website}
                          {r.websiteGuessed && <span style={{ color: 'var(--warm)' }}> · guessed</span>}
                        </>
                      ) : (
                        'find via search'
                      )}
                    </span>
                    <span className="tseg">{r.segment}</span>
                    <span className="tgo">{r.briefed ? 'Open brief →' : 'Brief →'}</span>
                  </button>
                ))}
            </div>
          </div>
        ))}
    </>
  )
}
